import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { BankAccount } from '@/types/external/2work/api/request/BankAccount.ts'

import { useReportIframeStore } from '@/iframeStores/reportIframeStore.ts'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI.ts'
import { useModalManagerStore } from '@/stores/modalManager.ts'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore.ts'
import { NetworkError } from '@/utilities/stores.ts'
import { translationFunctionDefault } from '@/utilities/translations.ts'

const useBankAccountStore = defineStore('bankAccountStore', () => {
  // translations
  const tfDefault = translationFunctionDefault()

  // pinia stores
  const frappeNestCustomAPIStore = useFrappeNestCustomAPIStore()
  const reportIframeStore = useReportIframeStore()
  const modalManagerStore = useModalManagerStore()
  const simpleNotificationStore = useSimpleNotificationStore()

  // refs
  const ibanBackendErrorMessage = ref<string | undefined>(undefined)

  const updateBankAccountHandler = async (
    targetName: string,
    bankAccountPayload: Partial<BankAccount>,
  ): Promise<BankAccount> => {
    return await frappeNestCustomAPIStore.updateBankAccount(targetName, bankAccountPayload)
  }

  const createBankAccountHandler = async (
    bankAccountPayload: BankAccount,
  ): Promise<BankAccount> => {
    return await frappeNestCustomAPIStore.createBankAccount(bankAccountPayload)
  }

  const after = (): void => {
    reportIframeStore.triggerIframeAction('refreshDoc', {})
    modalManagerStore.closeModal()
    simpleNotificationStore.triggerSimpleNotificationSuccess(
      tfDefault('editRelationGroup.editSuccess', {
        fieldName: tfDefault('fieldNames.bankAccount'),
      }),
    )
  }

  const onNetworkError = (networkError: unknown): void => {
    // iban error handling
    if (networkError instanceof NetworkError) {
      if (networkError.cause?.message.toLowerCase().includes('iban')) {
        const errorMsg = tfDefault('editRelationGroup.ibanError')
        simpleNotificationStore.triggerSimpleNotificationError(errorMsg)
        ibanBackendErrorMessage.value = errorMsg
      }
    }
  }

  return {
    // refs
    ibanBackendErrorMessage,

    // actions
    after,
    createBankAccountHandler,
    onNetworkError,
    updateBankAccountHandler,
  }
})

export { useBankAccountStore }
